import { atom, injectStore, injectMemo, injectSelf, injectEffect, injectAtomValue, injectRef, api } from '@zedux/react';
import { IFilter, IMessage, IMessagesStore, IContext } from '../types';
import { activeTopicAtom } from './atoms';
import { messageReceiverAtom } from './messageReceiverAtom';


export const messagesAtomFamily = atom('messages2', (topic_id: number, filter: IFilter, context: IContext) => {
    const store = injectStore<IMessagesStore>({
        mappedMessages: {} as { [key: number]: IMessage; },
        sortedIds: [] as number[],
    });

    const allIsLoadedRef = injectRef(false);


    const { mappedMessages, sortedIds } = store.getState();

    const messages: IMessage[] = injectMemo(() => {
        return sortedIds.map(id => mappedMessages?.[id] ?? null).filter(Boolean) as any as IMessage[];
    }, [mappedMessages, sortedIds]);

    const addMessage = (message: IMessage) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;

            // If message already exists, we update it but we don't change the order
            const newMap = { ...mappedMessages, [message.message_id]: { ...message } };
            const newSorted = [...sortedIds];

            if (mappedMessages[message.message_id]) {
                return { mappedMessages: newMap, sortedIds: newSorted };
            }

            const index = state.sortedIds.findIndex(id => mappedMessages[id].created_at > message.created_at);
            if (index === -1) {
                newSorted.push(message.message_id);
            } else {
                newSorted.splice(index, 0, message.message_id);
            }

            return { mappedMessages: newMap, sortedIds: newSorted };
        });
    };

    const addMessages = (messages: IMessage[]) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;
            const newIds = messages.map(m => m.message_id);
            const newMap = { ...mappedMessages };
            const newSorted = sortedIds.filter(id => !newIds.includes(id));

            messages.forEach(message => {
                newMap[message.message_id] = { ...message };
                const index = newSorted.findIndex(id => newMap[id].created_at > message.created_at);
                if (index === -1) {
                    newSorted.push(message.message_id);
                } else {
                    newSorted.splice(index, 0, message.message_id);
                }

            });
            return { mappedMessages: newMap, sortedIds: newSorted };
        });
    };

    const setMessageAsNotSent = (temporary_message_id: number) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;
            const message = mappedMessages[temporary_message_id];
            if (message) {
                const newMessage = { ...message, status: "error" } as IMessage;
                const newMap = { ...mappedMessages, [temporary_message_id]: newMessage };
                return { mappedMessages: newMap, sortedIds };
            }
            return state;
        });
    };

    const updateMessage = (updatedMessage: IMessage, resort: boolean) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;
            const message = mappedMessages[updatedMessage.message_id];
            if (message) {
                const newMap = { ...mappedMessages, [updatedMessage.message_id]: { ...message, ...updatedMessage } } as { [key: number]: IMessage; };
                if (resort) {
                    const newSorted = sortedIds.filter(id => id !== updatedMessage.message_id);
                    const index = newSorted.findIndex(id => newMap[id].created_at > updatedMessage.created_at);
                    if (index === -1) {
                        newSorted.push(updatedMessage.message_id);
                    } else {
                        newSorted.splice(index, 0, updatedMessage.message_id);
                    }
                    return { mappedMessages: newMap, sortedIds: newSorted };
                }
                return { mappedMessages: newMap, sortedIds: [...sortedIds] };
            }
            return state;
        });
    };

    const deleteMessage = (message_id: number) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state
            if (mappedMessages[message_id]) {
                const newMap = { ...mappedMessages }
                delete newMap[message_id]
                const newSorted = sortedIds.filter(id => id !== message_id)
                return { mappedMessages: newMap, sortedIds: newSorted }
            }
            return state
        })
    }

    const setMessageAsSent = (temporary_message_id: number, message: IMessage) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;
            // Find pending message with temporary ID
            const pendingMessage = mappedMessages[temporary_message_id];
            if (pendingMessage) {
                // Remove pending message and add new message with real ID
                const new_message_id = message.message_id;
                const { topic_id, ...newMessage } = message as any;
                const newMap = { ...mappedMessages, [new_message_id]: newMessage }
                // Delete Temporary Message
                delete newMap[temporary_message_id];

                // Add new message ID to sorted IDs
                const newSorted = sortedIds.filter(id => id !== temporary_message_id);
                const index = newSorted.findIndex(id => newMap[id].created_at > newMessage.created_at)
                if (index === -1) {
                    newSorted.push(new_message_id);
                } else {
                    newSorted.splice(index, 0, new_message_id);
                }
                return { mappedMessages: newMap, sortedIds: newSorted }
            }
            return state;
        });
    };

    const getMinimumAndMaximum = () => {
        const { sortedIds } = store.getState()
        if (sortedIds.length === 0) return [null, null]
        const leastId = sortedIds[0]
        const greatestId = sortedIds[sortedIds.length - 1]
        return [leastId, greatestId]
    };


    const getAllIsLoaded = () => {
        return allIsLoadedRef.current;
    };

    const setAsAllLoaded = () => {
        allIsLoadedRef.current = true;
    };

    const setMessagesAsSeen = (largest_seen_message_id: number) => {
        store.setState((state) => {
            const { mappedMessages, sortedIds } = state;
            const newMap = { ...mappedMessages };
            sortedIds.forEach(id => {
                if (id <= largest_seen_message_id) {
                    newMap[id] = { ...newMap[id], is_seen: true };
                }
            })
            return { mappedMessages: newMap, sortedIds };
        })
    }



    // Fetch once — only when this topic becomes the active topic
    const fetchedOnceRef = injectRef(false);

    const clearCache = () => {
        store.setState({
            mappedMessages: {} as { [key: number]: IMessage; },
            sortedIds: [] as number[],
        })
        allIsLoadedRef.current = false
        fetchedOnceRef.current = false
    }

    
    const isFetchedBefore = () => {
        return fetchedOnceRef.current;
    }

    const setFetchedOnce = () => {
        fetchedOnceRef.current = true;
    }

    return api(messages).setExports({
        addMessage, addMessages, setMessageAsNotSent,
        updateMessage, setMessageAsSent, getMinimumAndMaximum, getAllIsLoaded, setAsAllLoaded,
        setMessagesAsSeen, deleteMessage, clearCache, isFetchedBefore, setFetchedOnce
    })
});
